import type { ReleasedCrewJobDetail, ReleasedCrewJobListItem } from '../types/crewJobs';
import { CREW_COMMIT_THANK_YOU_MESSAGE } from './crewJobsRoster';

export const CREW_JOBS_API_BASE = 'https://us-central1-lctf-projects.cloudfunctions.net';
export const CREW_JOBS_RECAPTCHA_SITE_KEY = '6LdEep8tAAAAABox_iOqLzi3m_qDvXOHsKNuUX0v';

type ListResponse = { success: boolean; jobs?: ReleasedCrewJobListItem[]; error?: string };
type DetailResponse = { success: boolean; job?: ReleasedCrewJobDetail; error?: string };
type CommitResponse = { success: boolean; commitId?: string; message?: string; error?: string };

export async function fetchReleasedCrewJobs(): Promise<ReleasedCrewJobListItem[]> {
  const res = await fetch(`${CREW_JOBS_API_BASE}/listReleasedCrewJobs`);
  const data = (await res.json()) as ListResponse;
  if (!res.ok || !data.success) throw new Error(data.error || 'Could not load crew jobs');
  return data.jobs || [];
}

export async function fetchReleasedCrewJob(slug: string): Promise<ReleasedCrewJobDetail> {
  const res = await fetch(`${CREW_JOBS_API_BASE}/getReleasedCrewJob?slug=${encodeURIComponent(slug)}`);
  const data = (await res.json()) as DetailResponse;
  if (!res.ok || !data.success || !data.job) throw new Error(data.error || 'Job not found');
  return data.job;
}

export async function submitCrewJobCommit(input: {
  recaptchaToken: string;
  jobSlug: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  note?: string;
}): Promise<string> {
  const res = await fetch(`${CREW_JOBS_API_BASE}/commitToCrewJob`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const data = (await res.json()) as CommitResponse;
  if (!res.ok || !data.success) throw new Error(data.error || 'Could not submit commitment');
  return data.message || CREW_COMMIT_THANK_YOU_MESSAGE;
}

declare global {
  interface Window {
    grecaptcha?: {
      enterprise?: {
        ready: (cb: () => void) => void;
        execute: (siteKey: string, opts: { action: string }) => Promise<string>;
      };
    };
  }
}

let recaptchaLoader: Promise<void> | null = null;

function loadEnterpriseRecaptcha(): Promise<void> {
  if (window.grecaptcha?.enterprise) return Promise.resolve();
  if (recaptchaLoader) return recaptchaLoader;
  recaptchaLoader = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-crew-recaptcha="1"]');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('reCAPTCHA failed to load')));
      return;
    }
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(CREW_JOBS_RECAPTCHA_SITE_KEY)}`;
    script.async = true;
    script.defer = true;
    script.dataset.crewRecaptcha = '1';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('reCAPTCHA failed to load'));
    document.head.appendChild(script);
  });
  return recaptchaLoader;
}

export async function executeCrewJobRecaptcha(action = 'commit_crew_job'): Promise<string> {
  await loadEnterpriseRecaptcha();
  const enterprise = window.grecaptcha?.enterprise;
  if (!enterprise) throw new Error('reCAPTCHA is not available');
  await new Promise<void>(resolve => enterprise.ready(() => resolve()));
  return enterprise.execute(CREW_JOBS_RECAPTCHA_SITE_KEY, { action });
}

export function formatReleasedCrewJobDates(job: Pick<ReleasedCrewJobListItem, 'startDate' | 'endDate'>): string {
  const fmt = (iso: string) => new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  if (job.startDate && job.endDate) {
    const same = job.startDate.slice(0, 10) === job.endDate.slice(0, 10);
    return same ? fmt(job.startDate) : `${fmt(job.startDate)} – ${fmt(job.endDate)}`;
  }
  if (job.startDate) return fmt(job.startDate);
  if (job.endDate) return fmt(job.endDate);
  return 'Dates to be announced';
}
